import { observer } from "mobx-react";
import * as React from "react";
import { PageVehicleSettingsTab, routePath } from "../route-path";

import { ListVehicles } from "../ui/molecules/list-vehicles";
import { ListVehicleGroups } from "../ui/molecules/list-vehicle-groups";
import { ListCommandButtonsVehicles } from "../ui/organisms/list-command-buttons-vehicles";
import { createUuid } from "../utils/uuid";
import { type IPivotItemProps, Pivot, PivotItem, Stack } from "@fluentui/react";
import { Section } from "../ui/atoms/section";
import { ListCommandButtonsVehicleGroups } from "../ui/organisms/list-command-buttons-vehicle-groups";
import { ListVehicleAddButton } from "../ui/organisms/list-vehicle-add-button";
import { ListVehicleGroupAddButton } from "../ui/organisms/list-vehicle-group-add-button";
import { action, makeObservable, observable } from "mobx";
import { type Location } from "history";
import sizes from "../ui/sizes.scss";
import { articles } from "../help";
import { store } from "../store";
import { FusionLayout } from "@/parts/fusion-layout";
import { Forklift } from "lucide-react";

const customPivotItemRenderer = (
    link?: IPivotItemProps,
    defaultRenderer?: (link?: IPivotItemProps) => JSX.Element | null,
): JSX.Element | null => {
    if (!link || !defaultRenderer) {
        return null;
    }

    return (
        <span style={{ display: "flex", alignItems: "center" }}>
            <Forklift style={{ width: 20, paddingRight: 5, display: "inline-flex" }} />
            <div>{defaultRenderer({ ...link })}</div>
        </span>
    );
};

@observer
export class PageVehicleSettings extends React.Component {
    // Create the UUID on this level, so the button bar is able to access the same state as the list.
    private vehicleListStateId = createUuid();
    private vehicleGroupListStateId = createUuid();

    @observable private accessor selectedTab: string = PageVehicleSettingsTab.Vehicles;

    constructor(props: {}) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.historyProvider.history.listen(({ location }) => this.updateSelectedTab(location));
        this.updateSelectedTab(store.historyProvider.history.location);
    }

    @action.bound
    private updateSelectedTab(location: Location): void {
        // NOTE: when rewriting as a functional component use useLocation hook instead
        const tab = location.pathname.split("/").slice(-1)[0];

        if (typeof tab !== "string") {
            return;
        }

        this.selectedTab = tab;
    }

    // When the tab is changed, update the URL
    @action.bound
    private updateURL(item?: PivotItem): void {
        if (item?.props.itemKey) {
            store.historyProvider.history.push(
                routePath.vehicleSettings(item.props.itemKey as PageVehicleSettingsTab),
            );
        }
    }

    public render(): JSX.Element {
        return (
            <FusionLayout
                suggestedHelpArticles={[
                    articles.adminManageTrucks,
                    articles.howToAddTruck,
                    articles.howToEditTruck,
                    articles.howToRemoveTruck,
                    articles.howToCreateAccessControlGroups,
                    articles.administratorFunctionalities,
                ]}
                breadcrumb={store.i18n.t("page.vehicleSettings.title")}
                classic
            >
                <Pivot
                    linkSize="large"
                    styles={{
                        root: {
                            display: "flex",
                            justifyContent: "center",
                            height: sizes.pivotHeight,
                        },
                        link: { marginBottom: `${sizes.gutter}` },
                    }}
                    onLinkClick={this.updateURL}
                    selectedKey={this.selectedTab}
                >
                    <PivotItem
                        headerText={store.i18n.t("page.vehicleSettings.tab.vehicle")}
                        itemKey={PageVehicleSettingsTab.Vehicles}
                        onRenderItemLink={customPivotItemRenderer}
                    >
                        <Section>
                            <ListVehicles
                                includeInactive={true}
                                commandBar={
                                    <Stack horizontal style={{ alignItems: "center" }}>
                                        <ListVehicleAddButton
                                            listStateId={this.vehicleListStateId}
                                        />
                                        <ListCommandButtonsVehicles
                                            listStateId={this.vehicleListStateId}
                                            canDelete
                                        />
                                    </Stack>
                                }
                                listStateId={this.vehicleListStateId}
                                ignoreColumnVisibility={true}
                                maxHeight={`${sizes.listMaxHeightWithPivots}`}
                            />
                        </Section>
                    </PivotItem>
                    <PivotItem
                        headerText={store.i18n.t("page.vehicleSettings.tab.vehicleGroup")}
                        itemKey={PageVehicleSettingsTab.VehicleGroups}
                        onRenderItemLink={customPivotItemRenderer}
                    >
                        <Section>
                            <ListVehicleGroups
                                commandBar={
                                    <Stack horizontal>
                                        <ListVehicleGroupAddButton
                                            listStateId={this.vehicleGroupListStateId}
                                        />
                                        <ListCommandButtonsVehicleGroups
                                            listStateId={this.vehicleGroupListStateId}
                                            canDelete
                                        />
                                    </Stack>
                                }
                                listStateId={this.vehicleGroupListStateId}
                                maxHeight={`${sizes.listMaxHeightWithPivots}`}
                            />
                        </Section>
                    </PivotItem>
                </Pivot>
            </FusionLayout>
        );
    }
}
